import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import {
  AttachMoney as MoneyIcon,
  ConfirmationNumber as TicketIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import PrintJobsService from '../../services/printJobs';
import { ccyFormat, dateFormat } from '../../helpers/helper';
import TicketDetailModal from './TicketDetailModal';
import {
  STATUS_CONFIG,
  STATUS_OPTIONS,
  printerTypeLabel,
  PRINTER_TYPE_COLORS,
} from './ticketHelpers';

const PAGE_SIZE = 20;

const StatCard = ({ title, value, subtitle, icon: Icon, color, loading }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}30`,
      borderRadius: 3,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 24px ${color}40`,
      },
    }}
  >
    <CardContent>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box flex={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
            {title}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={90} height={48} sx={{ mt: 1 }} />
          ) : (
            <Typography variant="h4" fontWeight={700} color={color} mt={1} mb={0.5}>
              {value}
            </Typography>
          )}
          {subtitle && !loading && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: `${color}20`, color, width: 56, height: 56 }}>
          <Icon fontSize="large" />
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const firstOfMonth = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const TableSkeleton = ({ rows = 6, cols = 8 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <TableRow key={r}>
        {Array.from({ length: cols }).map((__, c) => (
          <TableCell key={c}><Skeleton variant="text" /></TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

const TicketsDashboard = ({ storeId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const now = new Date();

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [jobs, setJobs] = useState({ data: [], total: 0, page: 1, page_size: PAGE_SIZE });
  const [jobsLoading, setJobsLoading] = useState(true);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    from: firstOfMonth(now),
    to: isoDay(now),
    status: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const [selectedJob, setSelectedJob] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reprintingId, setReprintingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
  const showSnack = (severity, message) => setSnack({ open: true, severity, message });

  const debounceRef = useRef(null);

  const buildParams = useCallback((overrides = {}) => ({
    from: filters.from,
    to: filters.to,
    status: filters.status,
    q: searchQuery,
    page,
    page_size: PAGE_SIZE,
    ...overrides,
  }), [filters, searchQuery, page]);

  const loadStats = useCallback(async (params) => {
    if (!storeId) return;
    setStatsLoading(true);
    try {
      const data = await PrintJobsService.getPrintJobStats(storeId, params);
      setStats(data);
    } catch (err) {
      console.error('Error loading print job stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [storeId]);

  const loadJobs = useCallback(async (params) => {
    if (!storeId) return;
    setJobsLoading(true);
    setError('');
    try {
      const data = await PrintJobsService.getPrintJobs(storeId, params);
      setJobs({
        data: Array.isArray(data?.data) ? data.data : [],
        total: data?.total || 0,
        page: data?.page || params.page || 1,
        page_size: data?.page_size || PAGE_SIZE,
      });
    } catch (err) {
      console.error('Error loading print jobs:', err);
      setError('No se pudieron cargar los tickets. Intentá nuevamente.');
      setJobs({ data: [], total: 0, page: 1, page_size: PAGE_SIZE });
    } finally {
      setJobsLoading(false);
    }
  }, [storeId]);

  const refreshAll = useCallback((overrides = {}) => {
    const params = buildParams(overrides);
    loadStats(params);
    loadJobs(params);
  }, [buildParams, loadStats, loadJobs]);

  // Initial load + reload when store, date range, status or page change.
  useEffect(() => {
    if (!storeId) return;
    refreshAll();
  }, [storeId, filters.from, filters.to, filters.status, page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search: resets to page 1 and refreshes both endpoints.
  useEffect(() => {
    if (!storeId) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (page !== 1) {
        setPage(1); // page effect will trigger the reload
      } else {
        refreshAll({ page: 1 });
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setPage(1);
    setSearchQuery('');
    setFilters({ from: firstOfMonth(now), to: isoDay(now), status: '' });
  };

  const handleOpenDetail = (job) => {
    setSelectedJob(job);
    setDetailOpen(true);
  };

  const handleReprint = async (job) => {
    if (!job?._id) return;
    setReprintingId(job._id);
    try {
      await PrintJobsService.reprintPrintJob(job._id);
      showSnack('success', `Ticket #${job.ticket_number} reimpreso.`);
      refreshAll();
      setSelectedJob((prev) => (prev && prev._id === job._id
        ? { ...prev, print_count: (prev.print_count || 0) + 1, status: 'reprinted' }
        : prev));
    } catch (err) {
      console.error('Error reprinting:', err);
      showSnack('error', 'No se pudo reimprimir el ticket.');
    } finally {
      setReprintingId(null);
    }
  };

  const handleDownload = async (job) => {
    if (!job?._id) return;
    setDownloadingId(job._id);
    try {
      const blob = await PrintJobsService.downloadPrintJob(job._id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ticket-${job.ticket_number || job._id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      showSnack('error', 'No se pudo descargar el PDF.');
    } finally {
      setDownloadingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil((jobs.total || 0) / (jobs.page_size || PAGE_SIZE)));
  const rows = jobs.data;

  return (
    <Box sx={{ width: '100%', p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Stack direction="row" spacing={1} alignItems="center" mb={3}>
        <ReceiptIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Administración de Tickets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Historial de impresiones y métricas
          </Typography>
        </Box>
      </Stack>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Desde"
            type="date"
            value={filters.from}
            onChange={(e) => handleFilterChange('from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
          <TextField
            label="Hasta"
            type="date"
            value={filters.to}
            onChange={(e) => handleFilterChange('to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
          <TextField
            label="Buscar"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            fullWidth
            placeholder="N° ticket o cliente..."
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />,
              endAdornment: searchQuery ? (
                <ClearIcon
                  fontSize="small"
                  sx={{ cursor: 'pointer', color: 'text.secondary' }}
                  onClick={() => setSearchQuery('')}
                />
              ) : null,
            }}
          />
          <TextField
            label="Estado"
            select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            size="small"
            fullWidth
            sx={{ minWidth: { md: 160 } }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleResetFilters}
            sx={{ minWidth: { md: 130 }, whiteSpace: 'nowrap' }}
          >
            Limpiar
          </Button>
        </Stack>
      </Paper>

      {/* KPI Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total tickets"
            value={stats?.total_tickets ?? 0}
            subtitle="En el período"
            icon={TicketIcon}
            color="#42A5F5"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Facturación total"
            value={`$${ccyFormat(stats?.total_revenue || 0)}`}
            subtitle={`${stats?.revenue_tickets ?? 0} tickets facturados`}
            icon={MoneyIcon}
            color="#66BB6A"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Ticket promedio"
            value={`$${ccyFormat(stats?.avg_ticket || 0)}`}
            subtitle="Excluye fallidos"
            icon={ReceiptIcon}
            color="#AB47BC"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Reimpresiones"
            value={stats?.total_reprints ?? 0}
            subtitle="Total del período"
            icon={ReplayIcon}
            color="#FFA726"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Breakdown chips */}
      {!statsLoading && stats && (
        (Object.keys(stats.by_status || {}).length > 0 || Object.keys(stats.by_printer_type || {}).length > 0) && (
          <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              {Object.keys(stats.by_status || {}).length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                    Por estado
                  </Typography>
                  <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                    {Object.entries(stats.by_status).map(([key, count]) => (
                      <Chip
                        key={key}
                        size="small"
                        color={(STATUS_CONFIG[key] || {}).color || 'default'}
                        label={`${(STATUS_CONFIG[key] || {}).label || key}: ${count}`}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              {Object.keys(stats.by_printer_type || {}).length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                    Por tipo de impresora
                  </Typography>
                  <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                    {Object.entries(stats.by_printer_type).map(([key, count]) => (
                      <Chip
                        key={key}
                        size="small"
                        variant="outlined"
                        icon={<PrintIcon />}
                        label={`${printerTypeLabel(key)}: ${count}`}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Paper>
        )
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ p: { xs: 1, md: 2 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} px={1}>
          <Typography variant="h6" fontWeight={700}>Tickets</Typography>
          <Chip label={`${jobs.total} registros`} size="small" variant="outlined" />
        </Stack>

        {jobsLoading && <LinearProgress sx={{ mb: 1, borderRadius: 2 }} />}

        {error ? (
          <Box sx={{ py: 4 }}>
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={() => refreshAll()}>Reintentar</Button>}
            >
              {error}
            </Alert>
          </Box>
        ) : (
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                  <TableCell>N° Ticket</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell align="center">Ítems</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Impresora</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell align="center">Reimpr.</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobsLoading && rows.length === 0 && <TableSkeleton rows={6} cols={9} />}

                {!jobsLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Stack spacing={1} alignItems="center">
                        <ReceiptIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
                        <Typography variant="body2" color="text.secondary">No hay tickets</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((job) => {
                  const status = STATUS_CONFIG[job.status] || STATUS_CONFIG.printed;
                  return (
                    <TableRow
                      key={job._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleOpenDetail(job)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{job.ticket_number || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {job.printed_at ? dateFormat(job.printed_at) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{job.customer || 'Consumidor final'}</Typography>
                      </TableCell>
                      <TableCell align="center">{Array.isArray(job.items) ? job.items.length : 0}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>${ccyFormat(job.total || 0)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.8} alignItems="center">
                          <Typography variant="body2">{job.printer_name || '—'}</Typography>
                          <Chip
                            label={printerTypeLabel(job.printer_type)}
                            size="small"
                            color={PRINTER_TYPE_COLORS[job.printer_type] || 'default'}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.68rem' }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={status.label} color={status.color} size="small" />
                      </TableCell>
                      <TableCell align="center">{job.print_count ?? 0}</TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <IconButton size="small" title="Ver detalle" onClick={() => handleOpenDetail(job)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            title="Reimprimir"
                            disabled={reprintingId === job._id}
                            onClick={() => handleReprint(job)}
                          >
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                          {job.output_path && (
                            <IconButton
                              size="small"
                              title="Descargar PDF"
                              disabled={downloadingId === job._id}
                              onClick={() => handleDownload(job)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size={isMobile ? 'small' : 'medium'}
            />
          </Box>
        )}
      </Paper>

      <TicketDetailModal
        job={selectedJob}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onReprint={handleReprint}
        onDownload={handleDownload}
        reprinting={reprintingId === selectedJob?._id}
        downloading={downloadingId === selectedJob?._id}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TicketsDashboard;
